import React, { useState, useEffect } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { useRouter } from 'next/router'
import Script from 'next/script'

import Page from 'components/common/Page'
import Text from 'components/common/Text'
import Title from 'components/common/Title'
import Spinner from 'components/common/Spinner'

import SignupBlock from 'components/common/blocks/SignupBlockAlt'
import { useTenantSettings } from 'hooks/api/tenant/useTenantSettings'
import { useSelf } from 'components/providers/SelfProvider'
import Card from 'components/common/cards/Card'
import { useManyProperties } from 'hooks/api/useManyProperties'
import { useTenantProfile } from 'hooks/api/tenant/useTenantProfile'

import colors from 'utility/colors'
import getNested from 'utility/getNested'
import { usePartner } from 'components/providers/PartnerProvider'
import { useRedirectInactivePartner } from 'hooks/useRedirectInactivePartner'

export const SignupPage = ({ withPartner = false }) => {
	const router = useRouter()
	const [loaded, setLoaded] = useState(false)
	const { data: tenantProfile } = useTenantProfile()
	const { partner } = usePartner()

	const [propertyIds, setPropertyIds] = useState([])
	const { data: settings } = useTenantSettings()
	const { isPartner, isSignedIn } = useSelf()

	useRedirectInactivePartner()

	const { data: properties, isLoading } = useManyProperties(propertyIds)

	const loading = isLoading && propertyIds?.length

	let title = `2-minute signup - Get a fair tax value every year.`
	let description = `Signup and get started receiving a fair tax value every year.`

	useEffect(() => {
		if (loaded) return

		const { properties: propertyIds } = router?.query
		let decodedProperties = propertyIds && decodeURIComponent(propertyIds)
		const propertyIdsArray = decodedProperties ? decodedProperties.split(',') : []

		setLoaded(true)
		setPropertyIds(propertyIdsArray)
	}, [router, loaded])

	const primaryColor = tenantProfile?.theme?.primaryColor
	const secondaryColor = tenantProfile?.theme?.secondaryColor

	return (
		<>
			<Script async src='https://tag.simpli.fi/sifitag/4ebdac70-9a9b-013b-5dc1-0cc47a1f72a4' />
			<Page
				title={title}
				description={description}
				appendTitle={false}
				footer
				fluid
				hideSignup
				footerBackground={`${colors.WHITE}`}
				backgroundColor={colors.WHITE}
				withPartnerNav={withPartner}
			>
				<Container style={{ marginBottom: 60 }}>
					<Row style={{ marginTop: 50, marginBottom: 20 }}>
						<Col>
							<Title style={{ fontWeight: 400, display: 'inline' }}>
								SIGN UP for{' '}
								<span style={{ color: secondaryColor, fontWeight: 'bold' }}>
									{settings?.signupYear}
								</span>
							</Title>
							<Text>Get a fair tax value every year.</Text>
						</Col>
					</Row>
					<Row>
						<Col xs='12' md='12' lg='12'>
							<Card noShadow style={{ backgroundColor: colors.OFF_WHITE, padding: 30 }}>
								{isPartner || loading || isSignedIn ? (
									<Spinner size={60} type={'Grid'} style={{ marginTop: 30, marginBottom: 30 }} />
								) : (
									<SignupBlock
										agentId={withPartner ? partner?.data?.public?.partnerId : undefined}
										discountPercent={
											withPartner ? partner?.data?.public?.discountPercentage : undefined
										}
										renewalDiscountPercent={
											withPartner ? partner?.data?.public?.renewalDiscountPercentage : undefined
										}
										primaryColor={primaryColor}
										secondaryColor={secondaryColor}
										onUpdateProperties={(properties) => {
											let joinedProperties = properties
												?.map((property) => getNested(property, 'id'))
												.join(',')
											router.push(
												{
													pathname: withPartner ? '/partners/[slug]/signup' : 'signup',
													query: withPartner
														? {
																...router.query,
																slug: router.query.slug,
																properties: joinedProperties,
														  }
														: {
																...router.query,
																properties: joinedProperties,
														  },
												},
												undefined,
												{
													shallow: true,
												},
											)
										}}
										propertyArray={properties}
									/>
								)}
							</Card>
						</Col>
					</Row>
				</Container>
			</Page>
		</>
	)
}
